import { HubCamp } from "./hubCamp";
import { BalanceData, BossMechanic, rareArchetypeFor, wyrmArchetype } from "@highmarch/domain";
import type { Biome } from "../ui/chapters";
import { ENEMY_STAND_IN } from "./views/enemyStandIns";
import { DIRT_MOUND } from "./views/burrowFx";
import { HQ_EDGE, HQ_GRASS, HQ_PATH, HQ_PORTAL, HQ_RALLY_FLAG, HQ_ROCKS, HQ_SPECIAL, HQ_TROOP, hqEnemyName } from "./views/common";
import { FoliageView } from "./views/foliage";
import { DetailScatter } from "./views/detail";
import { TOWER_SHOT, UNIT_SHOT } from "./views/projectiles";
import { SkinKits, petLodName } from "./skins/kit/runtime";
import { WornSkins } from "./skins/wornSkins";
import { FORTRESS_KITS, TOWER_KITS } from "./skins/kits";
import { towerSkinModels } from "./skins/kit/towerModels";

/** A worn tower skin's own model suffix (big kits: TowerKit.model), or null. */
const kitModel = (skin: string): string | null => TOWER_KITS[skin]?.model ?? null;

/**
 * Which HQ GLBs a screen needs (ART_WOW_PASS Phase 3 §9, per-level loading). Everything used to load at boot: 183
 * models, ~320 MB of decoded textures, most of them for towers the player does not own, enemies and bosses of other
 * chapters, and two biomes' props that the current map never shows. A level now loads its own set — its towers at
 * every tier, its waves' enemies and boss, its biome's props and detail, its authored landmarks, the road kit — and
 * the hub a lighter one. The library disposes whatever the next screen does not list.
 *
 * Pure: names in, names out (filtered to what the manifest ships), so it is unit-tested without a GPU.
 */
export interface LevelAssetInput {
  /** Owned, non-base tower archetype ids: the deck can only offer these. */
  readonly towers: readonly string[];
  /** Balance enemy ids the waves spawn, including minions a boss mechanic summons. */
  readonly enemies: readonly string[];
  /** The level's boss enemy id. */
  readonly boss: string;
  readonly biome: Biome;
  /** Hand-placed prop model names (level terrain). */
  readonly props: readonly string[];
  /** The map has a gorge the road may bridge. */
  readonly chasm: boolean;
  /** Fortress skin (views/baseSkin.ts), or null for the stock fortress. */
  readonly skin: string | null;
  /**
   * The continuous ground mesh is up (GroundMesh.available). When it is, the grass, cliff, gap and lava tiles are
   * never instanced; they are only the fallback when the terrain maps did not ship.
   */
  readonly groundMesh: boolean;
  /**
   * M40: the Starfall Wyrm's balance id when the level can roll it (1 wave in 1,000, every level from `wyrmRules.fromLevel`,
   * Iron and Endless too), else absent. Its full model only: it never wears the far-zoom LOD (it is the showpiece).
   */
  readonly wyrm?: string;
  /** The chapter's rare's balance id when the level can roll one (also in `enemies`): its model loads without a `_lod1`. */
  readonly rare?: string;
  /**
   * The level can deal an Ascend card (branches.json `ascend.minLevel`, level 11 on): only then do the tier-IV branch
   * models and the units their creatures borrow load. Absent counts as true. Chapter 1 used to hold two tier-IV
   * models per owned tower (~1 MB each on the wire, several decoded) that no level of it can show (M31 memory pass).
   */
  readonly tier4?: boolean;
  /**
   * docs/PETS.md: the pet the run takes (a resumed battle's own; null: none). Absent: the pet equipped now, as the
   * worn set has it (a fresh run takes that one: the pets feature's prepareRun).
   */
  readonly pet?: string | null;
  /**
   * Pets v2/v3: the pets a wild encounter may bring here (each roams from its `roamFromLevel`): their stock models (or
   * the stand-in's while one has not shipped). A coat it rolls is fetched when the level is built (levelScene.ts).
   */
  readonly wildPets?: readonly string[];
}

export interface HubAssetInput {
  readonly baseTier: number;
  /** Owned, non-base towers with the tier the hub stands them at. */
  readonly towers: readonly { readonly id: string; readonly tier: number }[];
  readonly biome: Biome;
  readonly skin: string | null;
  readonly groundMesh: boolean;
}

const TIERS = [1, 2, 3] as const;

function base(out: Set<string>, tiers: readonly number[], skin: string | null): void {
  for (const t of tiers) { out.add(`base_t${t}`); if (skin) out.add(`base_t${t}__${skin}`); }
  // the skin's flying companions (fortress v2: the Dragonlord's drake, the Moonstone Keep's moon owls)
  const companion = skin ? Object.values(FORTRESS_KITS).find((k) => k.model === skin)?.companion : undefined;
  if (companion) out.add(companion.model);
}

/** A biome's scenery: blocker rocks and trees, landmarks, dressing, and the older blocker pool as the last fallback. */
function scenery(out: Set<string>, biome: Biome, available: ReadonlySet<string>): void {
  const pool = FoliageView.Pools[biome] ?? FoliageView.Pools.meadow;
  const all = [...pool.rocks, ...pool.trees, ...pool.landmarks, ...pool.dressing];
  for (const n of all) out.add(n);
  if (!all.some((n) => available.has(n))) for (const n of HQ_ROCKS) out.add(n);
  out.add(DetailScatter.CardModel);                        // the card-clump scatter (views/detail.ts)
  // micro detail: every shipped variant of a family, the family's fallback only when none shipped (views/detail.ts)
  for (const src of DetailScatter.Sets[biome] ?? DetailScatter.Sets.meadow) {
    const have = src.variants.filter((n) => available.has(n));
    for (const n of have.length ? have : [src.fallback]) out.add(n);
  }
}

/** Tiles only the fallback ground path instances (views/grid.ts) when the terrain maps are missing. */
function groundTiles(out: Set<string>): void {
  for (const n of HQ_GRASS) out.add(n);
  for (const n of Object.values(HQ_EDGE)) out.add(n);
  out.add("g_gap"); out.add("g_lava");
}

function ship(out: Set<string>, available: ReadonlySet<string>): Set<string> {
  return new Set([...out].filter((n) => available.has(n)).sort());
}

/**
 * The unit models tier-IV creatures borrow, by tower id (render/tier4Fx.ts). The Rangers' bowman has a far-zoom LOD
 * since troops v2 (2026-10-27): views/troops.ts spawns `troop_archer_lod1` far out, like the soldier's.
 */
const TIER4_UNITS: Readonly<Record<string, readonly string[]>> = { barracks: ["troop_archer", "troop_archer_lod1"], fire: ["enemy_flyer"] };

export function levelAssetNeeds(input: LevelAssetInput, available: ReadonlySet<string>): Set<string> {
  const out = new Set<string>();
  base(out, TIERS, input.skin);
  for (const id of input.towers) {
    for (const t of TIERS) out.add(`tower_${id}_t${t}`);
    const shot = TOWER_SHOT[id];
    if (shot) out.add(shot.model);
    // Tier IV (M21): each branch's own model once the ascend art ships (`tower_<id>_t4_<branch>`), and the units its
    // creatures borrow — the Rangers' Camp's bowmen, the Phoenix Roost's bird (render/tier4Fx.ts).
    if (input.tier4 === false) continue;
    for (const n of available) if (n.startsWith(`tower_${id}_t4_`) && !n.endsWith("_lod1") && !n.includes("__")) out.add(n);
    for (const n of TIER4_UNITS[id] ?? []) out.add(n);
  }
  for (const id of [...input.enemies, input.boss]) {
    const standIn = ENEMY_STAND_IN[id];                    // the M21 enemies wear a stand-in until their own art ships
    // (only while its own model is missing: once art/hq ships `enemy_<id>`, the stand-in is not loaded at all)
    if (standIn && !available.has(hqEnemyName(id))) { out.add(hqEnemyName(standIn)); out.add(`${hqEnemyName(standIn)}_lod1`); }
    out.add(hqEnemyName(id));
    // far-zoom LOD (views/enemies.ts); dropped by ship() if not shipped. Never a rare's: it always draws its full model
    if (id !== input.rare) out.add(`${hqEnemyName(id)}_lod1`);
    const shot = UNIT_SHOT[id];
    if (shot) out.add(shot.model);
  }
  // M40: the Starfall Wyrm (780 KB) loads with the level, never on demand: it must never fly out undrawn, and
  // the sim cannot wait for a model. One instance at most, its textures 512² like every enemy's.
  if (input.wyrm) out.add(hqEnemyName(input.wyrm));
  if (input.enemies.includes("burrower")) out.add(DIRT_MOUND);
  out.add(HQ_TROOP); out.add(`${HQ_TROOP}_lod1`); out.add(HQ_RALLY_FLAG);
  out.add(HQ_PORTAL.base); out.add(HQ_PORTAL.ring);
  // the road kit: every special tile, since road cards can lay any of them on any map
  for (const n of Object.values(HQ_PATH)) out.add(n);
  for (const s of Object.values(HQ_SPECIAL)) { if (!s) continue; out.add(s.straight); if (s.corner) out.add(s.corner); }
  if (input.chasm) for (const n of ["g_bridge_1", "g_bridge_2", "g_bridge_3", ...DetailScatter.ScreeModels]) out.add(n);
  if (!input.groundMesh) groundTiles(out);
  scenery(out, input.biome, available);
  for (const p of input.props) out.add(p);
  // the worn skins' kits and models (render/skins/kit), and the run's pet in its worn skin
  const worn = WornSkins.get();
  for (const n of SkinKits.assetNames({ ...worn, petsOwned: [], ...(input.pet !== undefined ? { pet: input.pet } : {}) })) out.add(n);
  // the worn tower kits' own models (big kits), every tier, and every branch when tier IV can be dealt here
  const owned = new Map([...worn.towers].filter(([id]) => input.towers.includes(id)));
  for (const n of towerSkinModels(owned, kitModel, TIERS, available, input.tier4 !== false)) out.add(n);
  // the wild pets that may roam here, in their stock coats (render/skins/kit/runtime.ts petAssetNames)
  for (const id of input.wildPets ?? []) for (const n of SkinKits.petAssetNames(id, null)) { out.add(n); out.add(petLodName(n)); }
  return ship(out, available);
}

export function hubAssetNeeds(input: HubAssetInput, available: ReadonlySet<string>): Set<string> {
  const out = new Set<string>();
  base(out, [Math.max(1, Math.min(3, input.baseTier))], input.skin);
  for (const t of input.towers) {
    out.add(`tower_${t.id}_t${Math.max(1, Math.min(3, t.tier))}`);
  }
  if (!input.groundMesh) groundTiles(out);
  hubScenery(out, input.biome, available);
  for (const n of HubCamp.assetNames()) out.add(n);     // the war camp round the line-up (hubCamp.ts)
  for (const n of SkinKits.assetNames(WornSkins.get())) out.add(n);
  // the worn tower kits' own models (big kits) at the tier the hub stands each tower at
  const worn = WornSkins.get().towers;
  for (const t of input.towers) {
    const skin = worn.get(t.id);
    if (skin) for (const n of towerSkinModels(new Map([[t.id, skin]]), kitModel, [Math.max(1, Math.min(3, t.tier))], available, false)) out.add(n);
  }
  return ship(out, available);
}

/**
 * The hub's scenery (render/hubScenery.ts): a sparse ring of the biome's trees, rocks and low dressing. No landmarks,
 * no card clumps and no micro-detail scatter — the hub's fixed shot doesn't draw them, and the next level's own set
 * (preloaded while the hub idles) brings them in when they're needed.
 */
function hubScenery(out: Set<string>, biome: Biome, available: ReadonlySet<string>): void {
  const pool = FoliageView.Pools[biome] ?? FoliageView.Pools.meadow;
  const all = [...pool.rocks, ...pool.trees, ...pool.dressing];
  for (const n of all) out.add(n);
  if (!all.some((n) => available.has(n))) for (const n of HQ_ROCKS) out.add(n);
}

/** The level's asset input from balance data: its spawn groups, its boss (and the boss's summons), its terrain. */
export function levelAssetInput(balance: BalanceData, level: number, owned: readonly boolean[], biome: Biome, skin: string | null, groundMesh: boolean, rareLevel = level): LevelAssetInput {
  const L = balance.level(level);
  const enemies = new Set(L.groups.map((g) => balance.enemies[g.enemyIndex].id));
  const boss = balance.enemies[L.bossEnemyIndex];
  // the Necromancer raises Grunts (archetype 0) beside itself (sim/systems/enemyMotion.ts)
  if (boss.bossMechanic === BossMechanic.Necromancer) enemies.add(balance.enemies[0].id);
  // a Summoner calls its imps (ENEMIES_V2)
  for (const id of [...enemies]) { const k = balance.enemies.find((e) => e.id === id)?.abilities; if (k && k.summonEnemy >= 0) enemies.add(balance.enemies[k.summonEnemy].id); }
  // M36: any wave from stage `fromStage` may turn out to be the chapter's rare (domain sim/rares.ts): its model
  // (ART's `enemy_rare_cN`, 512² textures) loads with every level that can roll one, so it never walks out undrawn.
  // M37: Iron rolls its level's; Endless the chapter of its *through* (`rareLevel`), not of the ground it borrows.
  const rare = rareLevel >= balance.spawnRules.rare.fromLevel ? rareArchetypeFor(balance, rareLevel) : -1;
  if (rare >= 0) enemies.add(balance.enemies[rare].id);
  const rareId = rare >= 0 ? balance.enemies[rare].id : undefined;
  // M40: the Starfall Wyrm rolls wherever a rare may, from `wyrmRules.fromLevel` (Endless at any *through*)
  const w = wyrmArchetype(balance), wyrm = w >= 0 && Math.max(level, rareLevel) >= balance.spawnRules.wyrm.fromLevel ? balance.enemies[w].id : undefined;
  return {
    towers: balance.towers.filter((t) => !t.isBase && owned[t.index]).map((t) => t.id),
    enemies: [...enemies],
    boss: boss.id,
    biome,
    props: (L.terrain?.props ?? []).map((p) => p.name),
    chasm: L.chasm.length > 0,
    skin,
    groundMesh,
    tier4: level >= balance.branches.pacing.minLevel,
    ...(wyrm ? { wyrm } : {}),
    ...(rareId ? { rare: rareId } : {}),
    ...(level >= balance.pets.wild.fromLevel ? { wildPets: balance.pets.list.filter((p) => p.roamFromLevel <= level).map((p) => p.id) } : {}),
  };
}
